import { escapeHtml } from '../domain/cards/parseCards';

export interface MarkdownHeading {
  level: number;
  text: string;
  id: string;
}

export function Markdown({ text, className = 'markdown', withHeadingIds = false }: { text: string; className?: string; withHeadingIds?: boolean }) {
  return <div className={className} dangerouslySetInnerHTML={{ __html: markdownToHtml(text, { withHeadingIds }) }} />;
}

export function extractHeadings(markdown: string, maxLevel = 4): MarkdownHeading[] {
  const seen = new Map<string, number>();
  return markdown
    .split(/\r?\n/)
    .map((line) => /^(#{1,6})\s+(.+?)\s*$/.exec(line))
    .filter((match): match is RegExpExecArray => match !== null)
    .filter((match) => match[1].length <= maxLevel)
    .map((match) => {
      const text = stripInlineMarkdown(match[2]);
      const id = uniqueSlug(text, seen);
      return { level: match[1].length, text, id };
    });
}

export function slugify(value: string) {
  return stripInlineMarkdown(value)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'abschnitt';
}

export function markdownToHtml(markdown: string, options: { withHeadingIds?: boolean } = {}) {
  const headingIds = new Map<string, number>();
  let html = escapeHtml(markdown);
  html = html.replace(/^(#{1,4})\s+(.+)$/gm, (_, hashes: string, title: string) => {
    const level = hashes.length;
    if (!options.withHeadingIds) return `<h${level}>${title}</h${level}>`;
    const text = stripInlineMarkdown(title);
    const id = uniqueSlug(text, headingIds);
    return `<h${level} id="${id}"><a class="heading-anchor" href="#${id}" aria-label="Abschnitt verlinken">#</a>${title}</h${level}>`;
  });
  html = html.replace(/```([\s\S]*?)```/g, (_, code) => `<pre><code>${String(code).trim()}</code></pre>`);
  html = html.replace(/`([^`\n]+)`/g, '<code>$1</code>');
  html = html.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/(^|\W)_([^_\n]+)_(?=\W|$)/g, '$1<em>$2</em>');
  html = html.replace(/\[([^\]\n]+)\]\(([^)\n]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
  html = html.replace(/^[-*]\s+(.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n?)+/g, (match) => `<ul>${match.replace(/\n/g, '')}</ul>`);
  html = html.replace(/\n{2,}/g, '</p><p>');
  html = `<p>${html}</p>`;
  html = html.replace(/<p>\s*<(h[1-4]|ul|pre)>/g, '<$1>');
  html = html.replace(/<\/(h[1-4]|ul|pre)>\s*<\/p>/g, '</$1>');
  html = html.replace(/\n/g, '<br />');
  return html;
}

function uniqueSlug(text: string, seen: Map<string, number>) {
  const base = slugify(text);
  const count = seen.get(base) ?? 0;
  seen.set(base, count + 1);
  return count === 0 ? base : `${base}-${count + 1}`;
}

function stripInlineMarkdown(value: string) {
  return value
    .replace(/<[^>]+>/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[`*_~]/g, '')
    .trim();
}
